import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import type { DriverNode } from '../../types';
import ProvenanceBadge from './ProvenanceBadge';
import { formatEvoValue } from './format';

export default function DriverTree() {
  const [params] = useSearchParams();
  const [selectedId, setSelectedId] = useState(params.get('driver') ?? undefined);
  const [orientation, setOrientation] = useState<'left_to_right' | 'top_down'>('left_to_right');
  const q = useQuery({
    queryKey: ['evolution', 'driver-tree', selectedId],
    queryFn: () => api.evolutionDriverTree(selectedId),
  });

  const selected = useMemo(
    () => q.data?.nodes.find((node) => node.node_id === (selectedId ?? q.data?.selected_node_id)) ?? q.data?.nodes[0],
    [q.data, selectedId],
  );

  if (q.isLoading) return <div className="panel"><div className="small faint">Loading driver tree...</div></div>;
  if (q.isError || !q.data) return <div className="panel"><div className="small" style={{ color: 'var(--red)' }}>Driver tree unavailable.</div></div>;
  if (q.data.nodes.length === 0) return <div className="panel"><div className="small faint">No driver path configured.</div></div>;

  return (
    <div className="panel" style={{ marginTop: 14 }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        <div className="qhead" style={{ margin: 0 }}>
          <span className="qdot" style={{ background: 'var(--azura)' }} />
          Northstar driver path
        </div>
        <div className="decision-mode">
          {(['left_to_right', 'top_down'] as const).map((mode) => (
            <button key={mode} type="button" className={orientation === mode ? 'on' : ''} onClick={() => setOrientation(mode)}>
              {mode === 'left_to_right' ? 'rail' : 'rollup'}
            </button>
          ))}
        </div>
      </div>
      {q.data.all_pending && <div className="tag amber" style={{ marginBottom: 10 }}>coefficients pending calibration</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(260px, 0.42fr)', gap: 14 }}>
        <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
          <div
            className="flow"
            aria-label="Driver DAG path"
            style={{
              minWidth: orientation === 'left_to_right' ? 720 : 520,
              alignItems: orientation === 'left_to_right' ? 'center' : 'stretch',
              flexDirection: orientation === 'left_to_right' ? 'row' : 'column-reverse',
            }}
          >
            {q.data.nodes.map((node, index) => (
              <FragmentNode
                key={node.node_id}
                node={node}
                isLast={index === q.data!.nodes.length - 1}
                selected={selected?.node_id === node.node_id}
                onSelect={() => {
                  setSelectedId(node.node_id);
                }}
              />
            ))}
          </div>
        </div>
        <div className="panel" style={{ background: 'var(--panel2)' }}>
          {selected ? <NodeDetail node={selected} /> : <div className="small faint">Select a driver.</div>}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line-soft)' }}>
            <div className="small faint" style={{ marginBottom: 6 }}>Hard gates</div>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {q.data.hard_constraints.map((constraint) => (
                <span key={constraint.constraint_id} className="tag red" title={constraint.rule_id}>
                  {constraint.constraint_id} gate
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FragmentNode({ node, isLast, selected, onSelect }: { node: DriverNode; isLast: boolean; selected: boolean; onSelect: () => void }) {
  return (
    <>
      <button
        type="button"
        className={`node ${selected ? 'hot' : ''}`}
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSelect();
        }}
        style={{ minWidth: 190, textAlign: 'left', cursor: 'pointer' }}
      >
        <div className="small faint" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>{node.node_type}</div>
        <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{node.label}</div>
        <div className="mono small" style={{ marginTop: 5 }}>{formatEvoValue(node.current_value, { compact: true })}</div>
        <div className="small faint" style={{ marginTop: 4 }}>
          {node.coefficient ? `coeff ${node.coefficient.calibration_status}` : 'apex rollup'}
          <ProvenanceBadge value={node.current_value} />
        </div>
        {node.rules.length > 0 && <span className="sev-chip elevated" style={{ marginTop: 6 }}>rule-bound</span>}
      </button>
      {!isLast && <span className="arrow">-&gt;</span>}
    </>
  );
}

function NodeDetail({ node }: { node: DriverNode }) {
  return (
    <div>
      <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Selected driver</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{node.label}</div>
      <div className="field"><div className="field-label">Current</div><div className="field-value">{formatEvoValue(node.current_value)}<ProvenanceBadge value={node.current_value} /></div></div>
      <div className="field"><div className="field-label">Target</div><div className="field-value">{node.target_value ? formatEvoValue(node.target_value) : 'terminal node'}</div></div>
      <div className="field"><div className="field-label">Calibration</div><div className="field-value">{node.coefficient ? node.coefficient.calibration_status : 'apex rollup'}</div></div>
      <div className="field"><div className="field-label">Downstream</div><div className="field-value">{node.downstream_nodes.length ? node.downstream_nodes.join(', ') : 'terminal leaf'}</div></div>
    </div>
  );
}
